/**
 * ImkerKrone – globale Shop-Interaktionen (layout/theme.liquid; seit 10/2026 auch layout/password.liquid für die
 * Über-uns-Vorschau – alle Teile prüfen vorher, ob ihre Elemente vorhanden sind)
 *
 * 1. In den Warenkorb: Der Haven-Warenkorb-Drawer öffnet sich NICHT mehr. Stattdessen fliegt der Artikel in den
 *    Warenkorb-Knopf im Header, der Knopf federt, Goldpartikel und eine kurze Bestätigung erscheinen.
 *    Technik: Haven löst nach jedem Hinzufügen "theme-cart:updated" mit detail.shouldOpenCart = true aus.
 *    Ein Capture-Listener auf document läuft vor Havens Listener und setzt shouldOpenCart auf false.
 * 2. Warenkorb-Knopf und Versandkostenfrei-Banner: Füllstand und Texte aus /cart.js (Shopify AJAX Cart API).
 * 3. Ladezustände für + / − / Entfernen im Warenkorb und für Schnellkauf-Buttons.
 * 4. Produktseite: Die Versandkostenfrei-Anzeige zeigt live, was die gewählte Menge bewirkt (reine Anzeige;
 *    abgerechnet wird ausschließlich von Shopify).
 * 5. "Passend dazu" (Product Recommendations API) und Schnellkauf-Knöpfe [data-ik-quick-add] (Cart API /cart/add.js),
 *    auch für mehrere Artikel auf einmal (data-ik-items, Starter-Sets).
 * 6. Varianten-Konfigurator, Scroll-Einblendungen, Ladezustand beim Sortieren/Filtern im Katalog.
 * 7. Fliegende Bienen um „Unser Versprechen“ auf der Startseite (nur PC).
 *
 * Alle Animationen respektieren "prefers-reduced-motion".
 */
(() => {
  if (window.__ikUi) return;
  window.__ikUi = true;

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  // Zustand der Vollbild-Ansichten am PC (siehe initFullpage)
  const fp = { ready: false, enabled: false, slides: [], animating: false, frame: 0, lastWheel: 0, quietUntil: 0, acc: 0 };
  const routes = () => window.routes || {};
  const cartUrl = () => routes().cart_url || '/cart';
  const cartAddUrl = () => routes().cart_add_url || '/cart/add';
  const MONEY_PLACEHOLDER = /\{\{\s*(\w+)\s*\}\}/;

  /* ------------------------------------------------------------------------
     Geldbeträge (nur Anzeige) im Format des Shops
     ------------------------------------------------------------------------ */
  function decodeEntities(value) {
    if (!value) return value;
    const textarea = document.createElement('textarea');
    textarea.innerHTML = value;
    return textarea.value;
  }

  function group(cents, precision, thousands, decimal) {
    const [whole, fraction] = (cents / 100).toFixed(precision).split('.');
    const grouped = whole.replace(/(\d)(?=(\d{3})+(?!\d))/g, `$1${thousands}`);
    return fraction ? `${grouped}${decimal}${fraction}` : grouped;
  }

  function formatMoney(cents) {
    const source = document.querySelector('[data-ik-cart-button]');
    const format = decodeEntities(source?.dataset.moneyFormat || '');
    if (format && MONEY_PLACEHOLDER.test(format)) {
      const key = format.match(MONEY_PLACEHOLDER)[1];
      const map = {
        amount: () => group(cents, 2, ',', '.'),
        amount_no_decimals: () => group(cents, 0, ',', '.'),
        amount_with_comma_separator: () => group(cents, 2, '.', ','),
        amount_no_decimals_with_comma_separator: () => group(cents, 0, '.', ','),
        amount_with_space_separator: () => group(cents, 2, ' ', ','),
        amount_with_period_and_space_separator: () => group(cents, 2, ' ', '.'),
        amount_with_apostrophe_separator: () => group(cents, 2, "'", '.'),
      };
      return format.replace(MONEY_PLACEHOLDER, (map[key] || map.amount)());
    }
    try {
      return new Intl.NumberFormat(source?.dataset.locale || 'de-DE', {
        style: 'currency',
        currency: source?.dataset.currency || 'EUR',
      }).format(cents / 100);
    } catch (error) {
      return group(cents, 2, '.', ',');
    }
  }

  function moneyNoZeros(cents) {
    return formatMoney(cents).replace(/[.,]00(?=\D*$)/, '');
  }

  /* ------------------------------------------------------------------------
     Warenkorb-Zustand: Knopf im Header + Banner
     ------------------------------------------------------------------------ */
  let lastCart = null;

  async function fetchCart() {
    const response = await fetch(`${cartUrl()}.js`, { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error(`cart.js ${response.status}`);
    return response.json();
  }

  function shippingState(cart, threshold) {
    const total = cart.total_price || 0;
    const remaining = Math.max(0, threshold - total);
    const fill = threshold > 0 ? Math.min(100, Math.round((total * 100) / threshold)) : 0;
    return { total, remaining, fill, complete: threshold > 0 && remaining === 0 && cart.item_count > 0 };
  }

  function setText(element, html) {
    if (element && element.innerHTML !== html) element.innerHTML = html;
  }

  function renderCartState(cart) {
    lastCart = cart;

    document.querySelectorAll('[data-ik-cart-button]').forEach((button) => {
      const threshold = Number(button.dataset.threshold) || 0;
      const state = shippingState(cart, threshold);
      button.style.setProperty('--ik-fill', `${state.fill}%`);
      button.classList.toggle('has-items', cart.item_count > 0);
      button.classList.toggle('is-complete', state.complete);
      button.dataset.total = String(cart.total_price);
      // Zähler auch für Knöpfe pflegen, die Haven nicht kennt (z. B. den schwebenden Knopf)
      const counter = button.querySelector('[data-cart-counter]');
      if (counter) {
        counter.classList.toggle('active', cart.item_count > 0);
        const count = counter.querySelector('[data-cart-count]');
        if (count) count.textContent = cart.item_count > 0 ? String(cart.item_count) : '';
      }
      button.setAttribute(
        'aria-label',
        cart.item_count > 0 ? `Warenkorb öffnen, ${cart.item_count} Artikel` : 'Warenkorb öffnen, leer'
      );
      const tip = button.querySelector('[data-ik-cart-tip]');
      if (tip && threshold > 0) {
        let text = `Versandkostenfrei ab ${moneyNoZeros(threshold)}`;
        if (cart.item_count > 0) text = state.complete ? 'Ihr Versand ist kostenlos' : `Noch ${formatMoney(state.remaining)} bis versandkostenfrei`;
        setText(tip, text);
      }
    });

    document.querySelectorAll('[data-ik-ship-banner]').forEach((banner) => {
      const threshold = Number(banner.dataset.threshold) || 0;
      const state = shippingState(cart, threshold);
      const wasComplete = banner.classList.contains('is-complete');
      banner.style.setProperty('--ik-fill', `${state.fill}%`);
      banner.classList.toggle('is-complete', state.complete);
      let html = `<span>Kostenloser Versand ab <strong>${moneyNoZeros(threshold)}</strong> Bestellwert</span>`;
      if (cart.item_count > 0) {
        html = state.complete
          ? '<span>Geschafft: Ihre Bestellung wird <strong>versandkostenfrei</strong> geliefert</span>'
          : `<span>Nur noch <strong>${formatMoney(state.remaining)}</strong> bis zum kostenlosen Versand</span>`;
      }
      const textElement = banner.querySelector('[data-ik-ship-text]');
      if (textElement && textElement.innerHTML !== html) {
        textElement.innerHTML = html;
        if (!reduceMotion) {
          banner.classList.remove('is-updated');
          void banner.offsetWidth;
          banner.classList.add('is-updated');
        }
      }
      if (state.complete && !wasComplete && !reduceMotion) {
        banner.classList.add('is-celebrating');
        setTimeout(() => banner.classList.remove('is-celebrating'), 2400);
      }
    });
  }

  let refreshTimer = null;
  function scheduleCartRefresh(delay = 60) {
    clearTimeout(refreshTimer);
    return new Promise((resolve) => {
      refreshTimer = setTimeout(async () => {
        try {
          const cart = await fetchCart();
          renderCartState(cart);
          resolve(cart);
        } catch (error) {
          resolve(null);
        }
      }, delay);
    });
  }

  /* ------------------------------------------------------------------------
     Quelle des letzten "In den Warenkorb"-Klicks merken (für die Flug-Animation)
     ------------------------------------------------------------------------ */
  const ADD_TRIGGERS = [
    '[data-add-product]',
    '[data-ik-quick-add]',
    '[data-ik-bundle-submit]',
    '.product-form__submit',
  ].join(',');
  let lastSource = null;

  // Bild, von dem der Artikel losfliegt: das zum Button gehörende Produktbild, sonst der Button selbst
  const SOURCE_SCOPES = [
    ['.ik-addons__item', '.ik-addons__media img'],
    ['.ik-sets__card', '.ik-sets__thumb img, .ik-sets__strip-thumb img'],
    ['.ik-bestsellers__lead', '.ik-bestsellers__lead-media img'],
    ['.ik-bestsellers__row', '.ik-bestsellers__thumb img'],
    ['.ik-cart-gap__item', '.ik-cart-gap__media img'],
    ['.ik-fbt', '.ik-fbt__item--main .ik-fbt__media img'],
    ['.ik-card-product', '.ik-card-product__image, .ik-card-product__placeholder'],
    ['section-product', '.product-media-gallery__media img, .product-media-gallery img'],
    ['.product-card', 'img'],
  ];

  function findSourceImage(button) {
    for (const [scopeSelector, imageSelector] of SOURCE_SCOPES) {
      const scope = button.closest(scopeSelector);
      if (!scope) continue;
      for (const image of scope.querySelectorAll(imageSelector)) {
        const rect = image.getBoundingClientRect();
        if (rect.width > 8 && rect.height > 8 && rect.bottom > 0 && rect.top < window.innerHeight) return image;
      }
      return null;
    }
    return null;
  }

  function productTitleFor(button) {
    if (button.dataset.productTitle) return button.dataset.productTitle;
    if (button.matches('[data-ik-bundle-submit]')) return 'Ihre Auswahl';
    const section = button.closest('section-product');
    const title = section?.querySelector('.ik-pdp__title, .product__title, h1, h2');
    return title ? title.textContent.trim() : '';
  }

  document.addEventListener(
    'click',
    (event) => {
      const button = event.target.closest(ADD_TRIGGERS);
      if (!button || button.disabled) return;
      const image = findSourceImage(button);
      const rect = (image || button).getBoundingClientRect();
      lastSource = {
        button,
        rect,
        src: image ? image.currentSrc || image.src : '',
        isPlaceholder: !image || /\.svg(\?|$)/.test(image.currentSrc || image.src || ''),
        title: productTitleFor(button),
        time: Date.now(),
      };
      if (button.matches('[data-add-product]')) setBusy(button, true);
    },
    true
  );

  function setBusy(element, busy) {
    if (!element) return;
    element.classList.toggle('ik-is-loading', busy);
    if (busy) element.setAttribute('aria-busy', 'true');
    else element.removeAttribute('aria-busy');
  }

  function clearAddBusy() {
    document.querySelectorAll('.ik-is-loading[data-add-product], .ik-is-loading[data-ik-quick-add]').forEach((el) => setBusy(el, false));
  }

  /* ------------------------------------------------------------------------
     Haven-Event abfangen: Drawer bleibt zu, stattdessen Animation
     ------------------------------------------------------------------------ */
  document.addEventListener(
    'theme-cart:updated',
    (event) => {
      const detail = event.detail;
      if (!detail || detail.shouldOpenCart !== true) {
        scheduleCartRefresh(250);
        return;
      }
      detail.shouldOpenCart = false;

      const failed = Boolean(detail.error) || Boolean(detail.cart && detail.cart.status);
      if (failed) {
        clearAddBusy();
        if (detail.error) showToast({ error: detail.error, title: detail.productTitle || lastSource?.title || '' });
        scheduleCartRefresh(250);
        return;
      }
      celebrate();
    },
    true
  );

  function inViewport(element) {
    if (!element || element.getClientRects().length === 0) return false;
    const rect = element.getBoundingClientRect();
    return rect.bottom > 0 && rect.top < window.innerHeight && rect.width > 0;
  }

  /* Schwebender Warenkorb-Knopf: erscheint, sobald der Knopf im Header nicht mehr zu sehen ist */
  const headerCartButton = () => document.querySelector('[data-ik-cart-button]:not([data-ik-cart-float])');
  const floatCartButton = () => document.querySelector('[data-ik-cart-float]');

  function setFloatVisible(visible) {
    const float = floatCartButton();
    if (!float) return;
    float.classList.toggle('is-visible', visible);
    if (visible) {
      float.removeAttribute('aria-hidden');
      float.removeAttribute('tabindex');
    } else {
      float.setAttribute('aria-hidden', 'true');
      float.setAttribute('tabindex', '-1');
    }
  }

  function initFloatingCart() {
    const header = headerCartButton();
    if (!floatCartButton() || !header || !('IntersectionObserver' in window)) return;
    new IntersectionObserver((entries) => {
      entries.forEach((entry) => setFloatVisible(!entry.isIntersecting));
    }).observe(header);
  }

  function visibleCartButton() {
    const header = headerCartButton();
    if (inViewport(header)) return header;
    const float = floatCartButton();
    if (float) {
      setFloatVisible(true);
      return float;
    }
    return header;
  }

  async function celebrate() {
    const source = lastSource && Date.now() - lastSource.time < 20000 ? lastSource : null;
    lastSource = null;
    clearAddBusy();

    const cartPromise = scheduleCartRefresh(0);
    const button = visibleCartButton();
    // Haven rendert den Kaufbereich nach dem Hinzufügen neu: Bestätigung erst danach am (neuen) Button zeigen
    setTimeout(() => markButtonAdded(source?.button), 350);

    if (reduceMotion || !button) {
      const cart = await cartPromise;
      bumpCartButton(button, false);
      showToast({ title: source?.title, cart });
      return;
    }

    if (button.matches('[data-ik-cart-float]')) await new Promise((r) => setTimeout(r, 180));
    await flyToCart(source, button);
    bumpCartButton(button, true);
    const cart = (await cartPromise) || lastCart;
    showToast({ title: source?.title, cart });
  }

  function markButtonAdded(button) {
    let target = button;
    if (!target || !target.isConnected) target = document.querySelector('.ik-pdp .product-form__submit');
    if (!target) return;
    target.classList.remove('ik-added');
    void target.offsetWidth;
    target.classList.add('ik-added');
    setTimeout(() => {
      target.classList.remove('ik-added');
      const fresh = document.querySelector('.ik-pdp .product-form__submit');
      fresh?.classList.remove('ik-added');
    }, 2200);
  }

  const FLY_BAG_SVG =
    '<svg viewBox="0 0 24 24" width="44%" height="44%" aria-hidden="true" focusable="false"><path d="M5.5 8.5h13l-1 11.2a1.5 1.5 0 0 1-1.5 1.3H8a1.5 1.5 0 0 1-1.5-1.3z" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round"/><path d="M9 10.5V7a3 3 0 0 1 6 0v3.5" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/></svg>';

  function flyToCart(source, button) {
    return new Promise((resolve) => {
      const target = button.getBoundingClientRect();
      const start = source?.rect || { left: window.innerWidth / 2 - 40, top: window.innerHeight / 2 - 40, width: 80, height: 80 };
      const size = Math.max(56, Math.min(132, Math.min(start.width, start.height)));
      const sx = start.left + start.width / 2;
      const sy = start.top + start.height / 2;
      const tx = target.left + Math.min(target.width, 64) / 2 + 6;
      const ty = target.top + target.height / 2;

      // Runde 4: abgerundete Karte mit Produktbild (sonst Taschen-Symbol) und „+1“-Plakette
      const fly = document.createElement('div');
      fly.className = 'ik-fly';
      fly.style.width = `${size}px`;
      fly.style.height = `${size}px`;
      if (source?.src && !source.isPlaceholder) {
        const picture = document.createElement('span');
        picture.className = 'ik-fly__img';
        picture.style.backgroundImage = `url("${source.src}")`;
        fly.appendChild(picture);
      } else {
        fly.classList.add('ik-fly--icon');
        fly.innerHTML = FLY_BAG_SVG;
      }
      fly.insertAdjacentHTML('beforeend', '<span class="ik-fly__badge">+1</span>');
      document.body.appendChild(fly);

      // Quadratische Bézierkurve mit Bogen nach oben, Weg mit easeInOut gesampelt
      const cx = (sx + tx) / 2 + (tx > sx ? -40 : 40);
      const cy = Math.min(sy, ty) - Math.max(120, Math.abs(sx - tx) * 0.25);
      const ease = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);
      const frames = [];
      const steps = 28;
      for (let i = 0; i <= steps; i++) {
        const t = ease(i / steps);
        const x = (1 - t) * (1 - t) * sx + 2 * (1 - t) * t * cx + t * t * tx;
        const y = (1 - t) * (1 - t) * sy + 2 * (1 - t) * t * cy + t * t * ty;
        const scale = 1 - 0.78 * t;
        const rotate = -8 * Math.sin(Math.PI * t);
        frames.push({
          transform: `translate(${x - size / 2}px, ${y - size / 2}px) scale(${i === 0 ? 0.6 : scale}) rotate(${rotate}deg)`,
          opacity: i === steps ? 0.2 : 1,
          offset: i / steps,
        });
      }
      // kleines "Aufnehmen" am Anfang
      frames.splice(1, 0, {
        transform: `translate(${sx - size / 2}px, ${sy - size / 2 - 14}px) scale(1.08) rotate(0deg)`,
        opacity: 1,
        offset: 0.08,
      });
      frames.forEach((frame, index) => {
        if (index > 1 && frame.offset <= 0.08) frame.offset = 0.081 + index * 0.0001;
      });

      const duration = Math.min(1050, 720 + Math.hypot(tx - sx, ty - sy) * 0.25);
      const animation = fly.animate(frames, { duration, easing: 'linear', fill: 'forwards' });
      const trail = setInterval(() => spawnTrail(fly), 55);
      animation.onfinish = () => {
        clearInterval(trail);
        fly.remove();
        resolve();
      };
      animation.oncancel = animation.onfinish;
    });
  }

  function spawnTrail(fly) {
    const rect = fly.getBoundingClientRect();
    if (!rect.width) return;
    const dot = document.createElement('span');
    dot.className = 'ik-fly-trail';
    dot.style.left = `${rect.left + rect.width / 2}px`;
    dot.style.top = `${rect.top + rect.height / 2}px`;
    document.body.appendChild(dot);
    setTimeout(() => dot.remove(), 700);
  }

  function bumpCartButton(button, withParticles) {
    if (!button) return;
    button.classList.remove('ik-bump');
    void button.offsetWidth;
    button.classList.add('ik-bump');
    setTimeout(() => button.classList.remove('ik-bump'), 900);
    if (!withParticles) return;

    const rect = button.getBoundingClientRect();
    const cx = rect.left + Math.min(rect.width, 64) / 2 + 6;
    const cy = rect.top + rect.height / 2;
    const ring = document.createElement('span');
    ring.className = 'ik-burst-ring';
    ring.style.left = `${cx}px`;
    ring.style.top = `${cy}px`;
    document.body.appendChild(ring);
    setTimeout(() => ring.remove(), 900);

    const count = 12;
    for (let i = 0; i < count; i++) {
      const particle = document.createElement('span');
      particle.className = `ik-particle${i % 3 === 0 ? ' ik-particle--light' : ''}`;
      const angle = (Math.PI * 2 * i) / count + Math.random() * 0.4;
      const distance = 34 + Math.random() * 34;
      particle.style.left = `${cx}px`;
      particle.style.top = `${cy}px`;
      document.body.appendChild(particle);
      particle.animate(
        [
          { transform: 'translate(-50%, -50%) scale(0.4) rotate(0deg)', opacity: 1 },
          {
            transform: `translate(calc(-50% + ${Math.cos(angle) * distance}px), calc(-50% + ${Math.sin(angle) * distance}px)) scale(1) rotate(${120 + Math.random() * 120}deg)`,
            opacity: 1,
            offset: 0.55,
          },
          {
            transform: `translate(calc(-50% + ${Math.cos(angle) * (distance + 14)}px), calc(-50% + ${Math.sin(angle) * (distance + 14) + 18}px)) scale(0.2) rotate(240deg)`,
            opacity: 0,
          },
        ],
        { duration: 760 + Math.random() * 260, easing: 'cubic-bezier(.2,.7,.3,1)', fill: 'forwards' }
      ).onfinish = () => particle.remove();
    }
  }

  /* ------------------------------------------------------------------------
     Bestätigung (Toast) unten rechts
     ------------------------------------------------------------------------ */
  let toastTimer = null;
  const CHECK_SVG =
    '<svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true" focusable="false"><path d="M5 12.5l4.2 4.2L19 7" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"/></svg>';
  const ALERT_SVG =
    '<svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true" focusable="false"><circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" stroke-width="2"/><path d="M12 7.5v5.5M12 16.5v.5" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"/></svg>';

  function escapeHtml(value) {
    return String(value || '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
  }

  function showToast({ title, cart, error }) {
    document.querySelectorAll('.ik-toast').forEach((t) => t.remove());
    clearTimeout(toastTimer);

    const toast = document.createElement('div');
    toast.className = `ik-toast ik-ui${error ? ' ik-toast--error' : ''}`;
    toast.setAttribute('role', error ? 'alert' : 'status');

    let shipping = '';
    const threshold = Number(document.querySelector('[data-ik-cart-button]')?.dataset.threshold) || 0;
    if (!error && cart && threshold > 0) {
      const state = shippingState(cart, threshold);
      shipping = state.complete
        ? '<p class="ik-toast__ship ik-toast__ship--done">Ihr Versand ist jetzt kostenlos.</p>'
        : `<p class="ik-toast__ship">Noch <strong>${formatMoney(state.remaining)}</strong> bis zum kostenlosen Versand.</p>
           <span class="ik-toast__bar" aria-hidden="true"><span style="width:${state.fill}%"></span></span>`;
    }

    toast.innerHTML = `
      <span class="ik-toast__icon">${error ? ALERT_SVG : CHECK_SVG}</span>
      <div class="ik-toast__body">
        <p class="ik-toast__title">${error ? 'Das hat nicht geklappt' : 'Liegt im Warenkorb'}</p>
        <p class="ik-toast__text">${error ? escapeHtml(error) : `${title ? `<strong>${escapeHtml(title)}</strong> ` : ''}wurde hinzugefügt.`}</p>
        ${shipping}
        ${error ? '' : '<div class="ik-toast__actions"><button type="button" class="ik-toast__open" data-ik-toast-open>Warenkorb ansehen</button><button type="button" class="ik-toast__continue" data-ik-toast-close>Weiter einkaufen</button></div>'}
      </div>
      <button type="button" class="ik-toast__close" data-ik-toast-close aria-label="Hinweis schließen">
        <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>
      </button>
      <span class="ik-toast__timer" aria-hidden="true"></span>`;
    document.body.appendChild(toast);

    const close = () => {
      clearTimeout(toastTimer);
      toast.classList.add('is-leaving');
      setTimeout(() => toast.remove(), reduceMotion ? 0 : 320);
    };
    const start = () => {
      clearTimeout(toastTimer);
      toastTimer = setTimeout(close, error ? 7000 : 5500);
    };
    toast.addEventListener('mouseenter', () => clearTimeout(toastTimer));
    toast.addEventListener('mouseleave', start);
    toast.addEventListener('focusin', () => clearTimeout(toastTimer));
    toast.querySelectorAll('[data-ik-toast-close]').forEach((b) => b.addEventListener('click', close));
    toast.querySelector('[data-ik-toast-open]')?.addEventListener('click', () => {
      close();
      document.dispatchEvent(new CustomEvent('theme-quickCart:open'));
    });
    requestAnimationFrame(() => toast.classList.add('is-visible'));
    start();
  }

  // Warenkorb-Knöpfe öffnen immer den Drawer (Haven-Delegate auf body), nie die Seite /cart.
  // Haven verhindert die Navigation nur beim ersten Knopf; ohne diesen Abfang führte der schwebende Knopf
  // (und das Lade-Skript für Links in theme.liquid) zur Warenkorbseite. Ohne JavaScript bleibt der Link nutzbar.
  // Nur abfangen, wenn der Drawer wirklich da ist (Abschnitt aktiv und Haven-Skript geladen); sonst führt der Link zu /cart.
  const quickCartReady = () =>
    Boolean(document.querySelector('section-quick-cart')) && Boolean(window.customElements?.get('section-quick-cart'));
  document.addEventListener(
    'click',
    (event) => {
      if (event.target.closest('[data-ik-cart-button]') && quickCartReady()) event.preventDefault();
    },
    true
  );

  // Bestellübersicht (/cart): War der Warenkorb beim Laden leer, rendert Haven statt des Formulars nur ein <div>.
  // Kommen danach Artikel ohne Neuladen hinzu (z. B. über die Vorschläge), stünde der Kassen-Knopf in keinem
  // Formular. Dann führt er direkt zur Shopify-Kasse.
  document.addEventListener('click', (event) => {
    const button = event.target.closest('.ik-cart-page__checkout');
    if (!button || button.form) return;
    event.preventDefault();
    window.location.href = cartUrl().replace(/cart\/?$/, 'checkout');
  });

  // Öffnet sich der Warenkorb, ist die Bestätigung überflüssig
  document.addEventListener('theme-quickCart:open', () => {
    document.querySelectorAll('.ik-toast').forEach((t) => t.remove());
  });

  /* ------------------------------------------------------------------------
     Schnellkauf-Knöpfe ohne eigenes Formular ([data-ik-quick-add]) über die Cart API
     ------------------------------------------------------------------------ */
  // Ein Artikel (data-variant-id) oder mehrere auf einmal (data-ik-items, z. B. Starter-Sets auf der Startseite)
  // Starter-Sets: Artikel mit mehreren Ausführungen (z. B. Größen) wählt der Kunde selbst ([data-ik-set-variant])
  const setChoices = (button) => [...(button.closest('[data-ik-set-scope]')?.querySelectorAll('[data-ik-set-variant]') || [])];
  document.addEventListener('change', (event) => {
    if (event.target.matches?.('[data-ik-set-variant]') && event.target.value) event.target.closest('.ik-sets__choice')?.classList.remove('is-missing');
  });

  function quickAddItems(button) {
    if (button.dataset.ikItems) {
      try {
        const items = JSON.parse(button.dataset.ikItems)
          .concat(setChoices(button).map((select) => ({ id: select.value, quantity: 1 })))
          .map((item) => ({ id: Number(item.id), quantity: Math.max(1, Number(item.quantity) || 1) }))
          .filter((item) => item.id > 0);
        if (items.length) return items;
      } catch (error) {
        /* ungültige Angabe: auf data-variant-id zurückfallen */
      }
    }
    return [{ id: Number(button.dataset.variantId), quantity: 1 }];
  }

  document.addEventListener('click', async (event) => {
    const button = event.target.closest('[data-ik-quick-add]');
    if (!button || button.getAttribute('aria-busy') === 'true') return;
    event.preventDefault();
    const missing = setChoices(button).find((select) => !select.value);
    if (missing) {
      missing.closest('.ik-sets__choice')?.classList.add('is-missing');
      missing.focus();
      showToast({ error: `Bitte wählen Sie zuerst die Ausführung für „${missing.dataset.ikSetLabel || 'diesen Artikel'}“ aus.` });
      return;
    }
    setBusy(button, true);
    try {
      const response = await fetch(`${cartAddUrl()}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ items: quickAddItems(button) }),
      });
      const data = await response.json();
      if (!response.ok || data.status) {
        document.dispatchEvent(
          new CustomEvent('theme-cart:updated', {
            detail: { shouldOpenCart: true, error: data.description || data.message || 'Der Artikel konnte nicht hinzugefügt werden.', productTitle: button.dataset.productTitle },
          })
        );
        return;
      }
      document.dispatchEvent(new CustomEvent('theme-cart:updated', { detail: { cart: data, shouldOpenCart: true } }));
    } catch (error) {
      showToast({ error: 'Bitte prüfen Sie Ihre Internetverbindung und versuchen Sie es erneut.' });
    } finally {
      setBusy(button, false);
    }
  });

  /* ------------------------------------------------------------------------
     + / − / Entfernen im Warenkorb (Drawer und Warenkorbseite): Warteschlange
     Haven schickt jede Änderung sofort und mit der Zeilennummer (line) an /cart/change. Bei schnellen Klicks
     auf mehrere Artikel laufen Anfragen parallel, die Zeilennummern verschieben sich nach dem ersten Entfernen
     und der zweite Klick geht verloren. Deshalb übernimmt ik-ui.js diese Knöpfe:
     - jeder Klick zählt sofort (Anzeige, Ladezustand), Anfragen laufen nacheinander
     - Artikel werden über ihren eindeutigen Schlüssel (item.key) angesprochen, nicht über die Zeile
     - mehrere Klicks auf denselben Artikel werden zur letzten Menge zusammengefasst
     - neu gerendert wird erst, wenn die Warteschlange leer ist (Haven: theme-cart:updated)
     ------------------------------------------------------------------------ */
  const cartChangeUrl = () => routes().cart_change_url || '/cart/change';
  const lineQueue = { chain: Promise.resolve(), pending: 0, targets: new Map(), sent: new Map() };

  function lineElements(key) {
    return [...document.querySelectorAll(`component-quantity-button[data-key="${window.CSS && CSS.escape ? CSS.escape(key) : key.replace(/"/g, '\\"')}"]`)]
      .map((component) => component.closest('[data-quantity-button-container]'))
      .filter((item, index, list) => item && list.indexOf(item) === index);
  }

  function showLineError(key, message) {
    lineElements(key).forEach((item) => {
      item.classList.remove('ik-busy', 'ik-removing');
      item.querySelectorAll('.ik-is-loading').forEach((el) => setBusy(el, false));
      const error = item.querySelector('[data-error-message]');
      if (error) error.textContent = message;
    });
    showToast({ error: message });
  }

  async function sendLineChange(key) {
    const quantity = lineQueue.targets.get(key);
    if (quantity === undefined || lineQueue.sent.get(key) === quantity) return;
    lineQueue.sent.set(key, quantity);
    try {
      const response = await fetch(`${cartChangeUrl()}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: key, quantity }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || data.status) {
        lineQueue.targets.delete(key);
        lineQueue.sent.delete(key);
        showLineError(key, data.description || data.message || 'Die Menge konnte nicht geändert werden.');
      }
    } catch (error) {
      lineQueue.targets.delete(key);
      lineQueue.sent.delete(key);
      showLineError(key, 'Bitte prüfen Sie Ihre Internetverbindung und versuchen Sie es erneut.');
    }
  }

  document.addEventListener(
    'click',
    (event) => {
      const button = event.target.closest('component-quantity-button .quantity-button');
      if (!button) return;
      const component = button.closest('component-quantity-button');
      const key = component && component.dataset.key;
      if (!key) return; // ohne Schlüssel übernimmt Haven wie bisher
      // Capture auf document: Haven-Handler am Button wird nicht mehr erreicht
      event.preventDefault();
      event.stopPropagation();

      const type = component.dataset.type;
      const known = lineQueue.targets.has(key);
      const current = known ? lineQueue.targets.get(key) : parseInt(component.dataset.currentQuantity, 10) || 0;
      let target = current;
      if (type === 'add') target = current + 1;
      else if (type === 'subtract') target = Math.max(0, current - 1);
      else if (type === 'remove') target = 0;
      if (known && target === current) return;
      lineQueue.targets.set(key, target);

      lineElements(key).forEach((item) => {
        item.classList.add('ik-busy');
        item.classList.toggle('ik-removing', target === 0);
        const count = item.querySelector('[data-item-quantity]');
        if (count) count.textContent = String(target);
        const error = item.querySelector('[data-error-message]');
        if (error) error.textContent = '';
      });
      setBusy(button, true);

      lineQueue.pending += 1;
      lineQueue.chain = lineQueue.chain
        .then(() => sendLineChange(key))
        .finally(() => {
          lineQueue.pending -= 1;
          if (lineQueue.pending === 0) {
            lineQueue.targets.clear();
            lineQueue.sent.clear();
            document.dispatchEvent(new CustomEvent('theme-cart:updated', { detail: { shouldOpenCart: false } }));
          }
        });
    },
    true
  );

  /* ------------------------------------------------------------------------
     Produktseite: Versandkostenfrei-Vorschau für die gewählte Menge
     ------------------------------------------------------------------------ */
  // Runde 5: Der sichtbare Versandbalken der Produktseite steht unter Bild und Kaufbereich ([data-ik-ship-target]).
  // Haven aktualisiert nur die versteckte Quelle im Kaufbereich ([data-ik-ship-source]); ihr Stand wird übernommen.
  function syncShippingTarget(scope) {
    const source = scope.querySelector('[data-ik-ship-source]');
    const target = scope.querySelector('[data-ik-ship-target]');
    if (!source || !target) return;
    if (target.innerHTML !== source.innerHTML) target.innerHTML = source.innerHTML;
  }

  function updateShippingPreview(scope) {
    const progress =
      scope.querySelector('[data-ik-ship-target] [data-ik-ship-progress]') || scope.querySelector('[data-ik-ship-progress]');
    if (!progress) return;
    const threshold = Number(progress.dataset.threshold) || 0;
    const cartTotal = Number(progress.dataset.cartTotal) || 0;
    const price = Number(progress.dataset.variantPrice) || 0;
    const fill = progress.querySelector('[data-ik-ship-preview-fill]');
    const hint = progress.querySelector('[data-ik-ship-preview]');
    if (!threshold || !fill || !hint) return;

    if (!price || cartTotal >= threshold) {
      fill.style.width = '0%';
      hint.hidden = true;
      return;
    }

    const input = scope.querySelector('[data-update-quantity] input[name="quantity"]');
    const quantity = Math.max(1, parseInt(input?.value, 10) || 1);
    const withSelection = cartTotal + price * quantity;
    const percent = Math.min(100, Math.round((withSelection * 100) / threshold));
    const subject = quantity > 1 ? `Mit ${quantity} Stück` : 'Mit diesem Artikel';
    const reached = withSelection >= threshold;
    const text = reached
      ? `${subject} ist Ihr Versand kostenlos.`
      : `${subject} fehlen noch ${formatMoney(threshold - withSelection)} bis zum kostenlosen Versand.`;

    const changed = fill.style.width !== `${percent}%`;
    fill.style.width = `${percent}%`;
    if (hint.textContent !== text) hint.textContent = text;
    hint.hidden = false;
    hint.classList.toggle('is-reached', reached);
    progress.classList.toggle('is-preview-reached', reached);
    if (changed && !reduceMotion) {
      progress.classList.remove('is-pulsing');
      void progress.offsetWidth;
      progress.classList.add('is-pulsing');
    }
  }

  /* Runde 5: Am PC enden Bild (links) und Kaufbereich bis zu den Express-Knöpfen (rechts) auf einer Linie.
     Ist der Kaufbereich höher als das quadratische Bild, wird das Bildfeld gestreckt (Bild bleibt ganz sichtbar,
     object-fit: contain). Ist er kürzer, schiebt CSS Menge und Kaufknöpfe nach unten (margin-top: auto). */
  function fitPdpMedia(section) {
    const media = section.querySelector('.ik-pdp__media');
    const details = section.querySelector('.ik-pdp__details');
    const gallery = media && media.querySelector('.product-media-gallery');
    const box = gallery && gallery.querySelector('.product-media-gallery__media');
    if (!media || !details || !box) return;
    media.removeAttribute('data-ik-fit');
    if (!window.matchMedia('(min-width: 64em)').matches) return;
    details.style.alignSelf = 'start';
    const detailsHeight = details.offsetHeight;
    details.style.alignSelf = '';
    const around = gallery.offsetHeight - box.offsetHeight;
    const target = Math.round(detailsHeight - around);
    if (target > box.offsetHeight + 4) {
      media.style.setProperty('--ik-media-h', `${target}px`);
      media.setAttribute('data-ik-fit', '');
    }
  }

  function initProductPage(section) {
    if (section.dataset.ikUiReady) return;
    section.dataset.ikUiReady = 'true';
    const fit = () => requestAnimationFrame(() => fitPdpMedia(section));
    fit();
    window.addEventListener('resize', fit, { passive: true });
    window.addEventListener('load', fit);
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(fit);
    const run = () => requestAnimationFrame(() => updateShippingPreview(section));
    section.addEventListener('click', (event) => {
      if (event.target.closest('.quantity__button')) run();
    });
    section.addEventListener('input', (event) => {
      if (event.target.matches('input[name="quantity"]')) run();
    });
    section.addEventListener('change', (event) => {
      if (event.target.matches('input[name="quantity"]')) run();
    });
    // Haven rendert Kaufbereich und Menge nach Varianten- und Warenkorbänderungen neu
    let pending = null;
    const observer = new MutationObserver(() => {
      clearTimeout(pending);
      pending = setTimeout(() => {
        syncShippingTarget(section);
        fitPdpMedia(section);
        updateShippingPreview(section);
        updateConfigSummary(section);
      }, 40);
    });
    const details = section.querySelector('.ik-pdp__details');
    if (details) observer.observe(details, { childList: true, subtree: true });
    run();
    initAddons(section);
    initConfigurator(section);
  }

  /* ------------------------------------------------------------------------
     Varianten-Konfigurator
     ------------------------------------------------------------------------ */
  function updateConfigSummary(section) {
    const config = section.querySelector('[data-ik-config]');
    if (!config) return;
    const summary = config.querySelector('[data-ik-config-summary]');
    const values = [...config.querySelectorAll('[data-product-option]')].map((option) => {
      const checked = option.querySelector('input:checked');
      return checked ? checked.value : '';
    }).filter(Boolean);
    const text = values.join(' · ');
    if (summary && text && summary.textContent.trim() !== text) {
      summary.textContent = text;
      if (!reduceMotion) {
        summary.classList.remove('is-flash');
        void summary.offsetWidth;
        summary.classList.add('is-flash');
      }
    }
  }

  function initConfigurator(section) {
    const config = section.querySelector('[data-ik-config]');
    if (!config) return;
    const toggle = config.querySelector('[data-ik-config-toggle]');
    const label = config.querySelector('[data-ik-config-toggle-label]');
    const panel = config.querySelector('[data-ik-config-panel]');
    if (!toggle || !panel) return;
    const setOpen = (open) => {
      config.classList.toggle('is-open', open);
      toggle.setAttribute('aria-expanded', String(open));
      if (label) label.textContent = open ? 'Auswahl übernehmen' : 'Jetzt konfigurieren';
      panel.inert = !open;
    };
    setOpen(false);
    toggle.addEventListener('click', () => {
      const open = !config.classList.contains('is-open');
      setOpen(open);
      if (open) setTimeout(() => panel.querySelector('input:checked, input')?.focus({ preventScroll: true }), 300);
    });
    // Auswahl zu Beginn sichtbar machen, wenn noch nichts Verfügbares gewählt ist
    if (section.querySelector('button.product-form__submit[disabled]')) setOpen(true);
  }

  /* ------------------------------------------------------------------------
     "Passend dazu": Shopify-Empfehlungen laden (complementary, sonst related)
     ------------------------------------------------------------------------ */
  async function initAddons(section) {
    const addons = section.querySelector('[data-ik-addons]');
    if (!addons || addons.dataset.loaded) return;
    addons.dataset.loaded = 'true';
    const base = addons.dataset.recommendationsUrl;
    const list = addons.querySelector('[data-ik-addons-list]');
    if (!base || !list) return;
    for (const intent of ['complementary', 'related']) {
      try {
        const response = await fetch(`${base}&intent=${intent}`);
        if (!response.ok) continue;
        const html = await response.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const rows = doc.querySelectorAll('[data-ik-addons-result] [data-ik-addon]');
        if (rows.length > 0) {
          list.replaceChildren(...[...rows].map((row) => document.importNode(row, true)));
          addons.hidden = false;
          return;
        }
      } catch (error) {
        /* Fallback (gleiche Produktart) bleibt stehen */
      }
    }
  }

  /* ------------------------------------------------------------------------
     Bienen um „Unser Versprechen“ ([data-ik-bees] = Anzahl)
     Nur am PC mit Maus und ohne „Bewegung reduzieren“. Jede Biene fliegt eine eigene, leicht unruhige Achterbahn
     um die Mitte der Sektion und blickt in Flugrichtung; auf die Maus reagieren sie nicht. Bewegt wird nur per transform;
     die Schleife läuft nur, solange die Sektion im Bild ist.
     ------------------------------------------------------------------------ */
  // Gleiche Biene wie die Maus-Biene (assets/bee-follower.js): Flügel nach hinten, Kopf oben
  const FLY_BEE_SVG =
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 36 36" fill="none" aria-hidden="true">' +
    '<g class="ik-bee-wing-left"><path d="M15 17C10 18 5 24 11 26C14.5 25.5 16.5 20.5 16 17.5Z" fill="#F5E5C9" fill-opacity="0.9" stroke="#E0B364" stroke-width="0.8"/></g>' +
    '<g class="ik-bee-wing-right"><path d="M21 17C26 18 31 24 25 26C21.5 25.5 19.5 20.5 20 17.5Z" fill="#F5E5C9" fill-opacity="0.9" stroke="#E0B364" stroke-width="0.8"/></g>' +
    '<ellipse cx="18" cy="21" rx="5" ry="7.5" fill="#E0B364"/>' +
    '<path d="M13.5 19H22.5M13 21.5H23M14 24H22" stroke="#1A140E" stroke-width="1.6" stroke-linecap="round"/>' +
    '<path d="M18 28.5L18 31" stroke="#E0B364" stroke-width="1.4" stroke-linecap="round"/>' +
    '<circle cx="18" cy="15" r="3.2" fill="#1A140E" stroke="#E0B364" stroke-width="0.8"/>' +
    '<circle cx="18" cy="9.5" r="2.8" fill="#1A140E" stroke="#E0B364" stroke-width="0.8"/>' +
    '<path d="M16.2 7.8L14 5.2M19.8 7.8L22 5.2" stroke="#F5E5C9" stroke-width="1.2" stroke-linecap="round"/></svg>';

  function initPromiseBees(section) {
    if (section.dataset.ikBeesReady) return;
    const allowed =
      !reduceMotion && window.matchMedia('(hover: hover) and (pointer: fine)').matches && window.innerWidth >= 992;
    if (!allowed || !('IntersectionObserver' in window)) return;
    section.dataset.ikBeesReady = 'true';

    const count = Math.min(8, Math.max(1, Number(section.dataset.ikBees) || 5));
    const layer = document.createElement('div');
    layer.className = 'ik-promise__bees';
    layer.setAttribute('aria-hidden', 'true');
    section.appendChild(layer);

    const bees = [];
    for (let i = 0; i < count; i++) {
      const el = document.createElement('span');
      el.className = 'ik-flybee';
      el.innerHTML = FLY_BEE_SVG;
      layer.appendChild(el);
      bees.push({
        el,
        size: 24 + Math.random() * 10,
        speed: 0.00018 + Math.random() * 0.00014,
        phase: (Math.PI * 2 * i) / count + Math.random() * 0.6,
        rx: 0.34 + Math.random() * 0.12,
        ry: 0.3 + Math.random() * 0.14,
        wobble: 10 + Math.random() * 14,
        wobbleSpeed: 0.002 + Math.random() * 0.002,
        x: 0,
        y: 0,
        angle: 0,
        started: false,
      });
    }
    bees.forEach((bee) => {
      bee.el.style.width = `${bee.size}px`;
      bee.el.style.height = `${bee.size}px`;
    });

    let width = 0;
    let height = 0;
    const measure = () => {
      width = section.clientWidth;
      height = section.clientHeight;
    };
    measure();
    window.addEventListener('resize', measure, { passive: true });

    let running = false;
    let frame = 0;
    const tick = (time) => {
      if (!running) return;
      const cx = width / 2;
      const cy = height / 2;
      for (const bee of bees) {
        const t = time * bee.speed + bee.phase;
        // Achterbahn (Lissajous 1:2) plus leichtes Zittern wie bei echten Bienen
        const tx = cx + Math.sin(t) * width * bee.rx + Math.sin(time * bee.wobbleSpeed + bee.phase) * bee.wobble;
        const ty = cy + Math.sin(t * 2) * height * bee.ry + Math.cos(time * bee.wobbleSpeed * 1.3) * bee.wobble;

        if (!bee.started) {
          bee.x = tx;
          bee.y = ty;
          bee.started = true;
        }
        const vx = tx - bee.x;
        const vy = ty - bee.y;
        bee.x = tx;
        bee.y = ty;
        if (Math.abs(vx) + Math.abs(vy) > 0.05) {
          const target = (Math.atan2(vy, vx) * 180) / Math.PI + 90;
          const delta = ((target - bee.angle + 540) % 360) - 180;
          bee.angle += delta * 0.12;
        }
        bee.el.style.transform = `translate3d(${bee.x - bee.size / 2}px, ${bee.y - bee.size / 2}px, 0) rotate(${bee.angle}deg)`;
      }
      frame = requestAnimationFrame(tick);
    };

    new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting && !running) {
          running = true;
          measure();
          layer.classList.add('is-flying');
          frame = requestAnimationFrame(tick);
        } else if (!entry.isIntersecting && running) {
          running = false;
          cancelAnimationFrame(frame);
          layer.classList.remove('is-flying');
        }
      });
    }).observe(section);
  }

  /* ------------------------------------------------------------------------
     Bühnen am PC ([data-ik-stage], z. B. „Unser Versprechen“, Über uns): Die Sektion wächst beim Hereinscrollen auf
     volle Bildschirmgröße, bleibt stehen und spielt beim Weiterscrollen einen Ablauf ab.
     position: sticky greift im Theme nicht (html/body haben overflow-x: hidden aus imkerkrone-design.css; das global
     zu ändern würde auch den Header kleben lassen). Deshalb klassisch: Die Bühne ist höher als der Bildschirm
     (--ik-stage-len), das Panel liegt davor oben (absolute), währenddessen fest (fixed), danach unten (absolute).
     Variablen auf der Bühne: --ik-e (1 → 0 beim Hereinscrollen), --ik-p (0 → 1 während sie steht) und je Phase aus
     data-ik-phases="name:von:bis,…" eine eigene Variable --ik-name (0 → 1).
     [data-ik-words]: Text wird in Wörter zerlegt (.ik-word, --i, --n), damit er sich einlesen kann.
     [data-ik-track]: horizontale Spur; --ik-track-x = wie weit sie verschoben werden muss (px, ohne Einheit).
     ------------------------------------------------------------------------ */
  function splitWords(element) {
    if (element.dataset.ikWordsDone) return;
    const words = element.textContent.trim().split(/\s+/);
    element.innerHTML = words.map((word, i) => `<span class="ik-word" style="--i: ${i}">${escapeHtml(word)}</span>`).join(' ');
    element.style.setProperty('--n', words.length);
    element.dataset.ikWordsDone = 'true';
  }

  function stagePhases(stage) {
    return (stage.dataset.ikPhases || '')
      .split(',')
      .map((part) => part.trim().split(':'))
      .filter((part) => part.length === 3)
      .map(([name, from, to]) => ({ name, from: Number(from), to: Number(to) }));
  }

  function applyStageProgress(stage, phases, progress) {
    stage.style.setProperty('--ik-p', progress.toFixed(4));
    phases.forEach(({ name, from, to }) => {
      const value = Math.min(1, Math.max(0, (progress - from) / (to - from || 1)));
      stage.style.setProperty(`--ik-${name}`, value.toFixed(4));
    });
  }

  /* Runde 4: In den Vollbild-Ansichten (siehe initFullpage) steht jede Bühne von Anfang an in voller Größe.
     Der Ablauf (--ik-p 0 → 1, Phasen) wird nicht mehr per Scrollen gesteuert, sondern beim Ankommen in der
     Ansicht direkt abgespielt (stage.ikPlay) und beim Verlassen zurückgesetzt (stage.ikReset). */
  function initStagePlay(stage) {
    stage.classList.add('ik-stage--pin', 'ik-stage--play');
    if (stage.ikPlay) return;
    stage.dataset.ikStageReady = 'play';
    stage.querySelectorAll('[data-ik-words]').forEach(splitWords);
    const phases = stagePhases(stage);
    const track = stage.querySelector('[data-ik-track]');
    if (track) stage.style.setProperty('--ik-track-x', 0);
    stage.style.setProperty('--ik-e', '0');
    let frame = 0;
    applyStageProgress(stage, phases, 0);
    stage.ikPlay = () => {
      cancelAnimationFrame(frame);
      const duration = Number(stage.dataset.ikPlay) || 4200;
      const start = performance.now();
      const tick = (now) => {
        const t = Math.min(1, (now - start) / duration);
        // schneller Beginn, ruhiges Auslaufen
        applyStageProgress(stage, phases, 1 - Math.pow(1 - t, 2.2));
        if (t < 1) frame = requestAnimationFrame(tick);
      };
      frame = requestAnimationFrame(tick);
    };
    stage.ikReset = () => {
      cancelAnimationFrame(frame);
      applyStageProgress(stage, phases, 0);
    };
  }

  function initStage(stage) {
    if (stage.dataset.ikStageReady) return;
    if (fp.enabled && stage.closest('[data-ik-slide]')) {
      initStagePlay(stage);
      return;
    }
    const query = window.matchMedia('(min-width: 64em) and (min-height: 640px) and (hover: hover) and (pointer: fine)');
    if (reduceMotion || !query.matches) return;
    stage.dataset.ikStageReady = 'true';
    stage.classList.add('ik-stage--pin');

    stage.querySelectorAll('[data-ik-words]').forEach(splitWords);
    const phases = stagePhases(stage);
    const track = stage.querySelector('[data-ik-track]');
    const measureTrack = () => {
      if (!track) return;
      stage.style.setProperty('--ik-track-x', Math.max(0, track.scrollWidth - track.clientWidth));
    };

    let state = '';
    const setState = (next) => {
      if (next === state) return;
      state = next;
      stage.classList.toggle('is-pinned', next === 'pinned');
      stage.classList.toggle('is-after', next === 'after');
    };
    const update = () => {
      const rect = stage.getBoundingClientRect();
      const vh = window.innerHeight;
      const travel = Math.max(1, rect.height - vh);
      if (rect.top > 0) setState('before');
      else if (rect.bottom > vh) setState('pinned');
      else setState('after');
      const enter = Math.min(1, Math.max(0, rect.top / vh));
      const progress = Math.min(1, Math.max(0, -rect.top / travel));
      stage.style.setProperty('--ik-e', enter.toFixed(4));
      applyStageProgress(stage, phases, progress);
    };

    let active = false;
    const onScroll = () => update();
    new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting && !active) {
          active = true;
          measureTrack();
          update();
          window.addEventListener('scroll', onScroll, { passive: true });
        } else if (!entry.isIntersecting && active) {
          active = false;
          update();
          window.removeEventListener('scroll', onScroll);
        }
      });
    }, { rootMargin: '200px 0px' }).observe(stage);
    window.addEventListener('resize', () => {
      measureTrack();
      update();
    }, { passive: true });
    measureTrack();
    update();
  }

  /* Kennzahlen zählen beim ersten Sichtbarwerden hoch ([data-ik-count] mit ganzer Zahl; echte Werte aus Shopify) */
  function initCounters() {
    const items = document.querySelectorAll('[data-ik-count]:not([data-ik-count-done])');
    if (!items.length || reduceMotion || !('IntersectionObserver' in window)) return;
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        observer.unobserve(entry.target);
        const el = entry.target;
        const target = parseInt(el.dataset.ikCount, 10);
        if (!Number.isFinite(target) || target <= 0) return;
        const start = performance.now();
        const duration = Math.min(1800, 700 + target * 12);
        const step = (now) => {
          const t = Math.min(1, (now - start) / duration);
          el.textContent = String(Math.round(target * (1 - Math.pow(1 - t, 3))));
          if (t < 1) requestAnimationFrame(step);
        };
        requestAnimationFrame(step);
      });
    }, { threshold: 0.6 });
    items.forEach((el) => {
      el.dataset.ikCountDone = 'true';
      observer.observe(el);
    });
  }

  /* ------------------------------------------------------------------------
     Scroll-Einblendungen
     ------------------------------------------------------------------------ */
  function initReveal() {
    // In den Vollbild-Ansichten steuert initFullpage das Einblenden (beim Ankommen, nicht beim Hereinscrollen)
    const items = [...document.querySelectorAll('[data-ik-reveal]:not(.is-revealed)')].filter(
      (el) => !(fp.enabled && el.closest('[data-ik-slide]'))
    );
    if (!items.length) return;
    if (reduceMotion || !('IntersectionObserver' in window)) {
      items.forEach((el) => el.classList.add('is-revealed'));
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-revealed');
            observer.unobserve(entry.target);
          }
        });
      },
      { rootMargin: '0px 0px -12% 0px', threshold: 0.08 }
    );
    items.forEach((el) => {
      el.classList.add('ik-reveal');
      observer.observe(el);
    });
  }

  /* ------------------------------------------------------------------------
     Katalog: sichtbarer Ladezustand beim Sortieren/Filtern
     ------------------------------------------------------------------------ */
  document.addEventListener('submit', (event) => {
    const form = event.target.closest('[data-ik-filter-form]');
    if (!form) return;
    form.closest('.ik-collection')?.querySelector('.ik-collection__results')?.classList.add('is-loading');
  });
  window.addEventListener('pageshow', () => {
    document.querySelectorAll('.ik-collection__results.is-loading').forEach((el) => el.classList.remove('is-loading'));
  });

  /* ------------------------------------------------------------------------
     Vollbild-Ansichten am PC (Runde 4): Startseite und Über uns bestehen aus Ansichten in Bildschirmgröße
     ([data-ik-slide], data-ik-slide="join" hängt eine Sektion an die vorherige Ansicht an, z. B. die
     Vertrauensleiste unter dem Hero). Ein Mausrad-Schritt bzw. Bild auf/ab, Pfeiltasten oder Leertaste führt
     mit einer sauberen, weichen Fahrt zur nächsten Ansicht. Beim Ankommen wird deren Animation sofort
     abgespielt (Klasse .is-active, [data-ik-fx]-Ebenen mit unterschiedlicher Tiefe = abgespielter Parallax,
     Bühnen per stage.ikPlay). Verlässt eine Ansicht den Bildschirm ganz, wird sie zurückgesetzt und spielt
     beim nächsten Besuch erneut.
     Ist eine Ansicht höher als der Bildschirm, wird innerhalb normal gescrollt, erst an ihrem Ende geht es
     weiter. Bei offenen Menüs, im Theme-Editor, auf Touch-Geräten, kleinen Fenstern und mit „Bewegung
     reduzieren“ bleibt alles beim normalen Scrollen. Die Grundklassen setzt ein kleines Skript im <head>
     (layout/theme.liquid), damit nichts aufblitzt.
     ------------------------------------------------------------------------ */
  const FP_QUERY = '(min-width: 64em) and (min-height: 600px) and (hover: hover) and (pointer: fine)';

  function fullpageAllowed() {
    const root = document.documentElement;
    return (
      root.hasAttribute('data-ik-fp-page') &&
      !reduceMotion &&
      !(window.Shopify && window.Shopify.designMode) &&
      window.matchMedia(FP_QUERY).matches
    );
  }

  function scrollLocked() {
    const root = document.documentElement;
    return (
      root.classList.contains('ik-scroll-locked') ||
      document.body.style.overflow === 'hidden' ||
      document.body.style.position === 'fixed' ||
      Boolean(document.querySelector('.cookie-banner-wrapper.is-open'))
    );
  }

  // Liegt unter dem Mauszeiger ein eigener Scrollbereich (Menü, Liste), der in diese Richtung noch scrollen kann?
  function scrollableInside(target, dy) {
    let el = target instanceof Element ? target : null;
    while (el && el !== document.body && el !== document.documentElement) {
      if (el.scrollHeight > el.clientHeight + 1 && /(auto|scroll)/.test(getComputedStyle(el).overflowY)) {
        if (dy > 0 && el.scrollTop + el.clientHeight < el.scrollHeight - 1) return true;
        if (dy < 0 && el.scrollTop > 0) return true;
      }
      el = el.parentElement;
    }
    return false;
  }

  const docTop = (el) => el.getBoundingClientRect().top + window.scrollY;

  function fpMeasure() {
    const vh = window.innerHeight;
    fp.slides.forEach((slide, index) => {
      // angehängte Sektionen inklusive Abständen zwischen ihnen
      const last = slide.els[slide.els.length - 1];
      const others = Math.max(0, last.getBoundingClientRect().bottom - slide.el.getBoundingClientRect().bottom);
      const offset = index === 0 ? Math.max(0, Math.round(docTop(slide.el))) : 0;
      slide.el.style.setProperty('--ik-fp-h', `${Math.max(360, vh - others - offset)}px`);
    });
  }

  function fpStops() {
    const stops = fp.slides.map((slide, index) => {
      const last = slide.els[slide.els.length - 1];
      return { top: index === 0 ? 0 : Math.round(docTop(slide.el)), bottom: Math.round(last.getBoundingClientRect().bottom + window.scrollY) };
    });
    const end = document.documentElement.scrollHeight;
    const lastStop = stops[stops.length - 1];
    // Fußbereich nach der letzten Ansicht
    if (lastStop && end - lastStop.bottom > 24) stops.push({ top: lastStop.bottom, bottom: end, tail: true });
    return stops;
  }

  // Ziel für einen Schritt in Richtung direction (1 = weiter, -1 = zurück): Zahl (Scrollposition) oder 'native'
  function fpDecide(direction) {
    const stops = fpStops();
    if (!stops.length) return 'native';
    const y = window.scrollY;
    const vh = window.innerHeight;
    const max = document.documentElement.scrollHeight - vh;
    let k = 0;
    stops.forEach((stop, index) => {
      if (stop.top <= y + 2) k = index;
    });
    const cur = stops[k];
    const tall = (stop) => stop.bottom - stop.top > vh + 4;
    if (direction > 0) {
      if (tall(cur) && y + vh < cur.bottom - 4) return 'native';
      const next = stops[k + 1];
      if (!next) return 'native';
      return Math.min(next.tail ? max : next.top, max);
    }
    if (y > cur.top + 4) {
      if (tall(cur) && y + vh <= cur.bottom + 4) return 'native';
      return tall(cur) ? Math.max(cur.top, cur.bottom - vh) : cur.top;
    }
    const prev = stops[k - 1];
    if (!prev) return 'native';
    return tall(prev) ? Math.max(prev.top, prev.bottom - vh) : prev.top;
  }

  function fpGo(target) {
    const start = window.scrollY;
    const distance = target - start;
    if (Math.abs(distance) < 2) return;
    cancelAnimationFrame(fp.frame);
    const duration = Math.min(1150, 720 + Math.abs(distance) * 0.22);
    const began = performance.now();
    const ease = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);
    fp.animating = true;
    document.documentElement.classList.add('ik-fp-moving');
    const step = (now) => {
      const t = Math.min(1, (now - began) / duration);
      window.scrollTo({ top: start + distance * ease(t), left: 0, behavior: 'instant' });
      if (t < 1) {
        fp.frame = requestAnimationFrame(step);
        return;
      }
      fp.animating = false;
      fp.quietUntil = performance.now() + 900;
      document.documentElement.classList.remove('ik-fp-moving');
    };
    fp.frame = requestAnimationFrame(step);
  }

  function fpOnWheel(event) {
    if (!fp.enabled || event.ctrlKey || scrollLocked()) return;
    const dy = event.deltaMode === 1 ? event.deltaY * 32 : event.deltaMode === 2 ? event.deltaY * window.innerHeight : event.deltaY;
    if (!dy || Math.abs(dy) < Math.abs(event.deltaX)) return;
    if (scrollableInside(event.target, dy)) return;
    const now = performance.now();
    const gap = now - fp.lastWheel;
    fp.lastWheel = now;
    if (fp.animating) {
      event.preventDefault();
      return;
    }
    // Nachlauf von Touchpad bzw. weiter drehendem Mausrad nach einer Fahrt schlucken
    if (now < fp.quietUntil && gap < 140) {
      event.preventDefault();
      return;
    }
    const decision = fpDecide(dy > 0 ? 1 : -1);
    if (decision === 'native') {
      fp.acc = 0;
      return;
    }
    event.preventDefault();
    if (gap > 220) fp.acc = 0;
    fp.acc += dy;
    if (Math.abs(fp.acc) < 18) return;
    fp.acc = 0;
    fpGo(decision);
  }

  function fpOnKey(event) {
    if (!fp.enabled || event.defaultPrevented || event.altKey || event.ctrlKey || event.metaKey || scrollLocked()) return;
    const target = event.target;
    if (target && (target.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName))) return;
    let direction = 0;
    switch (event.key) {
      case 'PageDown':
      case 'ArrowDown':
        direction = 1;
        break;
      case 'PageUp':
      case 'ArrowUp':
        direction = -1;
        break;
      case ' ':
        if (target && /^(BUTTON|A|SUMMARY)$/.test(target.tagName)) return;
        direction = event.shiftKey ? -1 : 1;
        break;
      default:
        return;
    }
    if (target && target.closest && target.closest('[role="listbox"], [role="menu"]')) return;
    if (fp.animating) {
      event.preventDefault();
      return;
    }
    const decision = fpDecide(direction);
    if (decision === 'native') return;
    event.preventDefault();
    fpGo(decision);
  }

  function slideStages(slide) {
    return slide.els.flatMap((el) => (el.matches('[data-ik-stage]') ? [el] : [...el.querySelectorAll('[data-ik-stage]')]));
  }

  function slideReveals(slide) {
    return slide.els.flatMap((el) => [...(el.matches('[data-ik-reveal]') ? [el] : []), ...el.querySelectorAll('[data-ik-reveal]')]);
  }

  function fpActivate(slide) {
    if (slide.active) return;
    slide.active = true;
    slide.els.forEach((el) => el.classList.add('is-active'));
    slideReveals(slide).forEach((el) => el.classList.add('is-revealed'));
    slideStages(slide).forEach((stage) => stage.ikPlay && stage.ikPlay());
  }

  function fpReset(slide) {
    if (!slide.active || !fp.enabled) return;
    slide.active = false;
    slide.els.forEach((el) => el.classList.remove('is-active'));
    slideReveals(slide).forEach((el) => el.classList.remove('is-revealed'));
    slideStages(slide).forEach((stage) => stage.ikReset && stage.ikReset());
  }

  function fpSetEnabled(enabled) {
    const root = document.documentElement;
    fp.enabled = enabled;
    root.classList.toggle('ik-fp', enabled);
    root.classList.toggle('ik-fp-ready', enabled);
    if (enabled) {
      fp.slides.forEach((slide) => slideStages(slide).forEach(initStagePlay));
      fpMeasure();
      // Ausgangszustand: nur die Ansicht in der Bildschirmmitte ist aktiv und spielt ihre Animation
      const mid = window.innerHeight / 2;
      fp.slides.forEach((slide) => {
        if (slide.active) {
          slide.active = true;
          fpReset(slide);
        }
        const centered = slide.els.some((el) => {
          const rect = el.getBoundingClientRect();
          return rect.top <= mid && rect.bottom >= mid;
        });
        if (centered) fpActivate(slide);
      });
      return;
    }
    // Zurück zum normalen Scrollen (z. B. Fenster verkleinert): alles sichtbar, Bühnen als normale Sektionen
    fp.slides.forEach((slide) => {
      slide.active = true;
      slide.els.forEach((el) => el.classList.add('is-active'));
      slideReveals(slide).forEach((el) => el.classList.add('is-revealed'));
      slideStages(slide).forEach((stage) => {
        if (stage.ikReset) stage.ikReset();
        stage.classList.remove('ik-stage--pin', 'ik-stage--play');
      });
    });
  }

  function initFullpage() {
    if (fp.ready) return;
    const root = document.documentElement;
    if (!fullpageAllowed()) {
      root.classList.remove('ik-fp', 'ik-fp-ready');
      return;
    }
    const nodes = [...document.querySelectorAll('[data-ik-slide]')];
    if (nodes.length < 2) {
      root.classList.remove('ik-fp', 'ik-fp-ready');
      return;
    }
    fp.ready = true;
    window.__ikFp = true;

    nodes.forEach((el) => {
      if (el.dataset.ikSlide === 'join' && fp.slides.length) fp.slides[fp.slides.length - 1].els.push(el);
      else fp.slides.push({ el, els: [el], active: false });
    });
    fp.slides.forEach((slide) => {
      slide.els.forEach((el) => {
        el.__ikSlide = slide;
        el.classList.add('ik-fp-slide');
      });
      // Einblendungen in Ansichten werden beim Ankommen abgespielt, nicht beim Hereinscrollen
      slideReveals(slide).forEach((el) => el.classList.add('ik-reveal'));
    });
    fpSetEnabled(true);

    // Aktiv wird eine Ansicht, sobald sie die Bildschirmmitte erreicht (während der Fahrt, kurz vor dem Ankommen)
    const band = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          entry.target.__ikBand = entry.isIntersecting;
          const slide = entry.target.__ikSlide;
          if (slide.els.some((el) => el.__ikBand)) fpActivate(slide);
        });
      },
      { rootMargin: '-42% 0px -42% 0px' }
    );
    // Zurückgesetzt wird erst, wenn sie ganz aus dem Bild ist (kein sichtbares Verschwinden)
    const visible = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          entry.target.__ikVisible = entry.isIntersecting;
          const slide = entry.target.__ikSlide;
          if (!slide.els.some((el) => el.__ikVisible)) fpReset(slide);
        });
      },
      // bloßes Berühren der Bildschirmkante zählt nicht als sichtbar
      { rootMargin: '-2px 0px -2px 0px' }
    );
    fp.slides.forEach((slide) =>
      slide.els.forEach((el) => {
        band.observe(el);
        visible.observe(el);
      })
    );

    window.addEventListener('wheel', fpOnWheel, { passive: false });
    document.addEventListener('keydown', fpOnKey);
    let resizeTimer = null;
    window.addEventListener(
      'resize',
      () => {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(() => {
          const allowed = fullpageAllowed();
          if (allowed !== fp.enabled) fpSetEnabled(allowed);
          else if (allowed) fpMeasure();
        }, 120);
      },
      { passive: true }
    );
    // Schriften und Bilder ändern Höhen (z. B. der angehängten Vertrauensleiste)
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(() => fp.enabled && fpMeasure());
    window.addEventListener('load', () => fp.enabled && fpMeasure());
  }

  /* ------------------------------------------------------------------------
     Scroll-Sperre bei offenen Menüs (Suche, Menü, Warenkorb) – Runde 4
     Haven sperrt per body { overflow: hidden }. Weil html in imkerkrone-design.css overflow-x: hidden hat, scrollt
     aber html (nicht body): Auf dem Handy lief die Seite unter den Suchergebnissen weiter und schob die Suche
     aus dem Bild. Solange Haven sperrt, wird deshalb auch html gesperrt (Klasse .ik-scroll-locked).
     ------------------------------------------------------------------------ */
  function initScrollLockSync() {
    const root = document.documentElement;
    const sync = () => {
      const locked = document.body.style.overflow === 'hidden' || document.body.style.position === 'fixed';
      root.classList.toggle('ik-scroll-locked', locked);
    };
    new MutationObserver(sync).observe(document.body, { attributes: true, attributeFilter: ['style'] });
    sync();
  }

  /* ------------------------------------------------------------------------
     Katalog: Sortierung als eigene Auswahlliste (Runde 4). Das native <select> bleibt im Formular und ohne
     JavaScript nutzbar; die Liste setzt nur seinen Wert und löst "change" aus (Formular sendet wie bisher).
     ------------------------------------------------------------------------ */
  const SORT_CHEVRON =
    '<svg class="ik-sort__caret" viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" focusable="false"><path d="M6 9l6 6 6-6" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';
  const SORT_CHECK =
    '<svg class="ik-sort__check" viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" focusable="false"><path d="M5 12.5l4.2 4.2L19 7" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/></svg>';
  const SORT_ICON =
    '<svg class="ik-sort__icon" viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" focusable="false"><path d="M7 4v16M7 20l-3-3M7 20l3-3M17 20V4M17 4l-3 3M17 4l3 3" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>';

  function initSortMenus() {
    document.querySelectorAll('.ik-sort:not([data-ik-sort-ready])').forEach((wrap) => {
      const select = wrap.querySelector('select.ik-sort__select');
      if (!select || !select.options.length) return;
      wrap.dataset.ikSortReady = 'true';
      wrap.classList.add('is-enhanced');
      const menuId = `${select.id || 'ik-sort'}-menu`;

      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'ik-sort__button';
      button.setAttribute('aria-haspopup', 'listbox');
      button.setAttribute('aria-expanded', 'false');
      button.setAttribute('aria-controls', menuId);
      button.innerHTML = `${SORT_ICON}<span class="ik-sort__button-text"><span class="ik-sort__button-label">Sortierung</span><span class="ik-sort__button-value"></span></span>${SORT_CHEVRON}`;

      const menu = document.createElement('ul');
      menu.className = 'ik-sort__menu';
      menu.id = menuId;
      menu.setAttribute('role', 'listbox');
      menu.setAttribute('aria-label', 'Sortieren nach');
      menu.tabIndex = -1;
      [...select.options].forEach((option, index) => {
        const item = document.createElement('li');
        item.className = 'ik-sort__option';
        item.id = `${menuId}-${index}`;
        item.setAttribute('role', 'option');
        item.dataset.value = option.value;
        item.style.setProperty('--ik-i', index);
        item.innerHTML = `<span>${escapeHtml(option.textContent.trim())}</span>${SORT_CHECK}`;
        menu.appendChild(item);
      });
      wrap.appendChild(button);
      wrap.appendChild(menu);

      const items = [...menu.children];
      let active = 0;
      const sync = () => {
        const current = select.selectedIndex < 0 ? 0 : select.selectedIndex;
        items.forEach((item, index) => item.setAttribute('aria-selected', String(index === current)));
        button.querySelector('.ik-sort__button-value').textContent = select.options[current].textContent.trim();
        return current;
      };
      const highlight = (index) => {
        active = (index + items.length) % items.length;
        items.forEach((item, i) => item.classList.toggle('is-active', i === active));
        menu.setAttribute('aria-activedescendant', items[active].id);
      };
      const isOpen = () => wrap.classList.contains('is-open');
      const open = () => {
        wrap.classList.add('is-open');
        button.setAttribute('aria-expanded', 'true');
        highlight(sync());
        menu.focus({ preventScroll: true });
      };
      const close = (focusButton) => {
        if (!isOpen()) return;
        wrap.classList.remove('is-open');
        button.setAttribute('aria-expanded', 'false');
        if (focusButton) button.focus({ preventScroll: true });
      };
      const choose = (index) => {
        close(true);
        if (index === select.selectedIndex) return;
        select.selectedIndex = index;
        sync();
        select.dispatchEvent(new Event('change', { bubbles: true }));
      };

      button.addEventListener('click', () => (isOpen() ? close(false) : open()));
      button.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
          event.preventDefault();
          open();
        }
      });
      menu.addEventListener('click', (event) => {
        const item = event.target.closest('.ik-sort__option');
        if (item) choose(items.indexOf(item));
      });
      menu.addEventListener('mousemove', (event) => {
        const item = event.target.closest('.ik-sort__option');
        if (item && !item.classList.contains('is-active')) highlight(items.indexOf(item));
      });
      menu.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowDown') highlight(active + 1);
        else if (event.key === 'ArrowUp') highlight(active - 1);
        else if (event.key === 'Home') highlight(0);
        else if (event.key === 'End') highlight(items.length - 1);
        else if (event.key === 'Enter' || event.key === ' ') choose(active);
        else if (event.key === 'Escape' || event.key === 'Tab') {
          close(event.key === 'Escape');
          return;
        } else return;
        event.preventDefault();
      });
      document.addEventListener('click', (event) => {
        if (!wrap.contains(event.target)) close(false);
      });
      select.addEventListener('change', sync);
      sync();
    });
  }

  /* ------------------------------------------------------------------------
     Akkordeons (.ik-accordion--smooth): weiches Auf- und Zuklappen, bleibt natives <details>
     ------------------------------------------------------------------------ */
  document.addEventListener('click', (event) => {
    const summary = event.target.closest('.ik-accordion--smooth .ik-accordion__summary');
    if (!summary || reduceMotion) return;
    const details = summary.parentElement;
    const content = details.querySelector(':scope > .ik-accordion__content');
    if (!content || !content.animate) return;
    event.preventDefault();
    if (details.dataset.ikAnimating) return;
    details.dataset.ikAnimating = 'true';
    const opening = !details.open;
    if (opening) details.open = true;
    // Runde 6h: Höhe UND Innenabstand animieren. Vorher blieb der Innenabstand (20 + 26 px) stehen – die Karte
    // schrumpfte nur bis dahin und sprang am Ende zu („etappenweise“). Beim Zuklappen gleichmäßige Kurve,
    // Plus/Minus und Symbol wechseln sofort (.is-closing).
    const cs = getComputedStyle(content);
    const height = content.getBoundingClientRect().height || content.scrollHeight;
    const full = { height: `${height}px`, paddingTop: cs.paddingTop, paddingBottom: cs.paddingBottom, opacity: 1 };
    const zero = { height: '0px', paddingTop: '0px', paddingBottom: '0px', opacity: 0 };
    content.style.overflow = 'hidden';
    details.classList.toggle('is-closing', !opening);
    const animation = content.animate(opening ? [zero, full] : [full, zero], {
      duration: opening ? 420 : 380,
      easing: opening ? 'cubic-bezier(0.22, 1, 0.36, 1)' : 'cubic-bezier(0.4, 0, 0.2, 1)',
    });
    const done = () => {
      if (!opening) details.open = false;
      details.classList.remove('is-closing');
      content.style.overflow = '';
      delete details.dataset.ikAnimating;
    };
    animation.onfinish = done;
    animation.oncancel = done;
  });

  /* ------------------------------------------------------------------------
     Waagerechte Produktreihen ([data-ik-rail], z. B. „Das könnte auch passen“): Pfeile blättern
     ------------------------------------------------------------------------ */
  function updateRail(rail) {
    const track = rail.querySelector('[data-ik-rail-track]');
    if (!track) return;
    const max = track.scrollWidth - track.clientWidth;
    rail.classList.toggle('is-scrollable', max > 4);
    const prev = rail.querySelector('[data-ik-rail-prev]');
    const next = rail.querySelector('[data-ik-rail-next]');
    if (prev) prev.disabled = track.scrollLeft <= 4;
    if (next) next.disabled = track.scrollLeft >= max - 4;
  }

  // Haven ersetzt den Inhalt der Empfehlungen nachträglich: deshalb alles über delegierte Ereignisse
  function initRails() {
    document.querySelectorAll('[data-ik-rail]').forEach(updateRail);
  }

  document.addEventListener(
    'scroll',
    (event) => {
      const track = event.target instanceof Element ? event.target.closest('[data-ik-rail-track]') : null;
      if (track) requestAnimationFrame(() => updateRail(track.closest('[data-ik-rail]')));
    },
    { capture: true, passive: true }
  );
  document.addEventListener('mouseover', (event) => {
    const rail = event.target instanceof Element ? event.target.closest('[data-ik-rail]:not([data-ik-rail-seen])') : null;
    if (!rail) return;
    rail.dataset.ikRailSeen = 'true';
    updateRail(rail);
  });
  window.addEventListener('resize', () => document.querySelectorAll('[data-ik-rail]').forEach(updateRail), { passive: true });
  if ('MutationObserver' in window) {
    // Empfehlungen werden nachgeladen: Pfeile danach einmal aktualisieren
    document.addEventListener('DOMContentLoaded', () => {
      document.querySelectorAll('component-recommended-products').forEach((component) => {
        new MutationObserver(() => component.querySelectorAll('[data-ik-rail]').forEach(updateRail)).observe(component, { childList: true });
      });
    });
  }

  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-ik-rail-prev], [data-ik-rail-next]');
    if (!button) return;
    const track = button.closest('[data-ik-rail]')?.querySelector('[data-ik-rail-track]');
    if (!track) return;
    const direction = button.matches('[data-ik-rail-next]') ? 1 : -1;
    track.scrollBy({ left: direction * Math.max(240, track.clientWidth * 0.8), behavior: reduceMotion ? 'auto' : 'smooth' });
  });

  /* Cookie-Knopf: Auf dem Handy nach getroffener Wahl ausgeblendet (Klasse .ik-consented, CSS in ik-r4.css).
     Die Wahl selbst speichert assets/cookie-banner.js über die Shopify Customer Privacy API. */
  function initCookieDock() {
    const wrapper = document.getElementById('cookie-banner-wrapper');
    if (!wrapper || wrapper.dataset.ikDock) return;
    wrapper.dataset.ikDock = 'true';
    const consented = () => wrapper.classList.add('ik-consented');
    const release = () => setTimeout(() => wrapper.classList.remove('is-summoned'), 500);
    try {
      // gleiche Regel wie in assets/cookie-banner.js: nur eine von Shopify bestätigte Wahl, höchstens 12 Monate alt
      const savedAt = Number(localStorage.getItem('imker_cookie_saved_at'));
      if (localStorage.getItem('imker_cookie_interacted') === 'true' && savedAt > 0 && Date.now() - savedAt < 365 * 864e5) consented();
    } catch (error) {
      /* Speicher gesperrt: Knopf bleibt sichtbar */
    }
    ['cookie-save-btn', 'cookie-accept-all-btn', 'cookie-reject-all-btn'].forEach((id) =>
      document.getElementById(id)?.addEventListener('click', () => {
        consented();
        release();
      })
    );
    document.getElementById('cookie-back-btn')?.addEventListener('click', release);
    document.getElementById('cookie-banner-overlay')?.addEventListener('click', release);
  }

  /* Cookie-Einstellungen über einen Link öffnen (Footer), da der Cookie-Knopf auf dem Handy nach der Wahl ausgeblendet ist */
  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-ik-cookie-open]');
    if (!trigger) return;
    const wrapper = document.getElementById('cookie-banner-wrapper');
    if (!wrapper) return;
    event.preventDefault();
    wrapper.classList.add('is-summoned');
    if (wrapper.classList.contains('is-closed')) wrapper.click();
  });

  /* ------------------------------------------------------------------------
     Runde 6: Sprungziele auf derselben Seite aus Warenkorb und Menüs (z. B. „Starter-Sets ansehen“ im leeren
     Warenkorb auf der Startseite). Vorher blieb der Warenkorb offen, und Havens Scroll-Sperre stellte beim Schließen
     die alte Position wieder her (Sprung nach oben). Jetzt: Panel schließen, Freigabe abwarten, dann weich hinfahren
     (Vollbild-Ansichten über fpGo). Links auf andere Seiten bleiben normale Links.
     ------------------------------------------------------------------------ */
  function pageUnlocked() {
    const root = document.documentElement;
    return (
      !root.classList.contains('ik-scroll-locked') &&
      root.style.overflow !== 'hidden' &&
      document.body.style.position !== 'fixed' &&
      document.body.style.overflow !== 'hidden'
    );
  }

  function scrollToAnchor(target) {
    const max = document.documentElement.scrollHeight - window.innerHeight;
    const top = Math.max(0, Math.min(max, Math.round(target.getBoundingClientRect().top + window.scrollY)));
    if (fp.enabled) {
      fpGo(top);
      return;
    }
    window.scrollTo({ top, left: 0, behavior: reduceMotion ? 'auto' : 'smooth' });
  }

  document.addEventListener(
    'click',
    (event) => {
      if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const link = event.target.closest && event.target.closest('a[href*="#"]');
      const panel = link && link.closest('[data-sub-id]');
      if (!panel) return;
      const url = new URL(link.href, window.location.href);
      if (url.origin !== window.location.origin || url.pathname !== window.location.pathname || url.hash.length < 2) return;
      const target = document.getElementById(decodeURIComponent(url.hash.slice(1)));
      if (!target) return;
      event.preventDefault();
      document.dispatchEvent(new CustomEvent(`theme-${panel.dataset.subId}:close`));
      const began = performance.now();
      const wait = () => {
        if (!pageUnlocked() && performance.now() - began < 1500) {
          requestAnimationFrame(wait);
          return;
        }
        // ein Frame Luft, damit Haven die alte Position vollständig zurückgesetzt hat
        requestAnimationFrame(() => scrollToAnchor(target));
      };
      requestAnimationFrame(wait);
    },
    true
  );

  /* ------------------------------------------------------------------------
     Runde 6: Angebots-Countdown (snippets/ik-sale-timer.liquid). Das Ende kommt aus dem Produkt-Metafeld
     bzw. Kollektions-Metafeld custom.sale_ends_at (Sekunden seit 1970 in data-ik-sale-end). Ein Takt pro Sekunde für
     alle Zähler, auch für Zähler, die Haven beim Variantenwechsel neu rendert. Runde 6n: Nach Ablauf verschwindet das
     ganze Angebot; ebenso alles mit data-ik-sale-until (Streichpreis, Plakette, Ersparnis), falls die Seite älter ist
     als das Angebot (offener Tab, zwischengespeicherte Seite).
     ------------------------------------------------------------------------ */
  const pad2 = (n) => String(n).padStart(2, '0');
  const SALE_SELECTOR = '[data-ik-sale-end], [data-ik-sale-until]';
  let saleTimer = 0;

  function endSaleDisplays(now) {
    document.querySelectorAll('[data-ik-sale-until]').forEach((el) => {
      if (Number(el.dataset.ikSaleUntil) - now > 0) return;
      el.removeAttribute('data-ik-sale-until');
      // Havens Preis-Snippet: ohne price--on-sale zeigt es nur den normalen Preis (kein Streichpreis)
      if (el.classList.contains('price')) el.classList.remove('price--on-sale');
      else el.hidden = true;
    });
  }

  function tickSaleTimers() {
    const now = Date.now() / 1000;
    endSaleDisplays(now);
    const timers = document.querySelectorAll('[data-ik-sale-end]');
    if (!document.querySelector(SALE_SELECTOR)) {
      clearInterval(saleTimer);
      saleTimer = 0;
      return;
    }
    timers.forEach((el) => {
      const left = Math.floor(Number(el.dataset.ikSaleEnd) - now);
      // das Angebot (Band bzw. Leiste) trägt Klassen für Optik; der Zähler selbst ist el
      const offer = el.closest('.ik-sale') || el;
      if (!(left > 0)) {
        // Angebot abgelaufen: Zähler und das ganze Angebot ausblenden
        el.hidden = true;
        el.removeAttribute('data-ik-sale-end');
        offer.classList.remove('ik-sale--timed', 'is-final');
        offer.hidden = true;
        return;
      }
      const parts = { d: Math.floor(left / 86400), h: Math.floor((left % 86400) / 3600), m: Math.floor((left % 3600) / 60), s: left % 60 };
      el.querySelectorAll('[data-ik-sale-part]').forEach((node) => {
        const text = pad2(parts[node.dataset.ikSalePart]);
        if (node.textContent === text) return;
        node.textContent = text;
        // kurzes Umklappen der Ziffer (nur Optik, CSS)
        node.classList.remove('is-tick');
        void node.offsetWidth;
        node.classList.add('is-tick');
      });
      const compact = el.querySelector('[data-ik-sale-compact]');
      // über einen Tag ohne Sekunden (passt auch auf schmale Karten)
      if (compact) compact.textContent = parts.d > 0 ? `${parts.d} T ${pad2(parts.h)}:${pad2(parts.m)}` : `${pad2(parts.h)}:${pad2(parts.m)}:${pad2(parts.s)}`;
      offer.classList.toggle('is-final', left < 3600);
    });
  }

  function initSaleTimers() {
    if (saleTimer || !document.querySelector(SALE_SELECTOR)) return;
    tickSaleTimers();
    saleTimer = setInterval(tickSaleTimers, 1000);
  }

  /* Runde 6d: Datum der Rückgabe-Garantie (snippets/ik-cart-guarantee.liquid) = heute + Tage, im Browser gesetzt */
  function initGuaranteeDates() {
    document.querySelectorAll('[data-ik-guarantee]:not([data-ik-date-set])').forEach((box) => {
      const days = Number(box.dataset.days) || 0;
      const target = box.querySelector('[data-ik-guarantee-date]');
      box.setAttribute('data-ik-date-set', '');
      if (!days || !target) return;
      const until = new Date();
      until.setDate(until.getDate() + days);
      // ausgeschrieben, z. B. „8. November 2026“
      const months = ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'];
      target.textContent = `${until.getDate()}. ${months[until.getMonth()]} ${until.getFullYear()}`;
    });
  }

  // Haven ersetzt den Preisbereich beim Variantenwechsel: neue Zähler wieder anmelden
  new MutationObserver(() => {
    if (document.documentElement && document.querySelector('[data-ik-guarantee]:not([data-ik-date-set])')) initGuaranteeDates();
    if (!saleTimer && document.documentElement && document.querySelector(SALE_SELECTOR)) initSaleTimers();
  }).observe(document.documentElement, { childList: true, subtree: true });

  /* ------------------------------------------------------------------------
     Runde 6m: Bereiche (Produktarten) als Mehrfachauswahl im Katalog, z. B. Beuten + Werkzeuge
     - Ist in „Search & Discovery“ der Filter „Produktart“ eingerichtet, wird ?bereich=… in den echten Shopify-Filter
       übersetzt (filter.p.product_type) und Shopify filtert selbst.
     - Sonst: Der Gesamtkatalog zeigt nur die Artikel der gewählten Bereiche. Artikel, Preise, Sortierung und die übrigen
       Filter kommen weiter von Shopify; liegen die Artikel auf mehreren Seiten, werden die anderen Seiten (gleiche
       Sortierung und Filter) per Section Rendering nachgeladen und in Shopifys Reihenfolge angezeigt.
     Okt 2026: Suche im Katalog (?suche=…) statt Shopify-Suchseite. Jedes Wort muss in Titel, Produktart, Hersteller,
     Tags oder Beschreibung vorkommen (data-ik-search, auch Wortteile, ä = ae, ß = ss). Treffer im Titel stehen oben.
     Suche und Bereiche lassen sich kombinieren; Filter, Sortierung und Preis-Schnellwahl behalten den Suchbegriff.
     ------------------------------------------------------------------------ */
  const AREA_PARAM = 'bereich';
  const SEARCH_PARAM = 'suche';

  const searchNorm = (value) =>
    String(value || '')
      .toLowerCase()
      .replace(/ä/g, 'ae')
      .replace(/ö/g, 'oe')
      .replace(/ü/g, 'ue')
      .replace(/ß/g, 'ss')
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .replace(/[^a-z0-9]+/g, ' ')
      .trim();

  function initAreas() {
    const root = document.querySelector('.ik-collection');
    if (!root || root.dataset.ikAreasInit) return;
    root.dataset.ikAreasInit = 'true';
    const params = new URLSearchParams(window.location.search);
    const chosen = [...new Set(params.getAll(AREA_PARAM).map((t) => t.trim()).filter(Boolean))];
    const term = (params.get(SEARCH_PARAM) || '').trim();

    if (root.dataset.ikTypeFilter === 'true') {
      if (chosen.length) {
        params.delete(AREA_PARAM);
        chosen.forEach((t) => params.append('filter.p.product_type', t));
        window.location.replace(window.location.pathname + '?' + params.toString() + window.location.hash);
        return;
      }
      if (!term) {
        root.classList.remove('is-areas-pending');
        return;
      }
      applySearchContext(root, term, []);
      filterAreas(root, [], term);
      return;
    }

    const allUrl = root.dataset.ikAllUrl || '/collections/all';
    const onAreasPage = root.hasAttribute('data-ik-areas-page');
    // Ausgangsauswahl: im Gesamtkatalog die URL, auf einer Typseite (/collections/types?q=…) deren Bereich
    const base = onAreasPage ? chosen : root.dataset.ikCurrentType ? [root.dataset.ikCurrentType] : [];
    const urlFor = (list) => {
      const next = onAreasPage ? new URLSearchParams(window.location.search) : new URLSearchParams();
      next.delete(AREA_PARAM);
      next.delete('page');
      list.forEach((t) => next.append(AREA_PARAM, t));
      const query = next.toString();
      return allUrl + (query ? '?' + query : '') + '#katalog';
    };

    root.querySelectorAll('[data-ik-area]').forEach((link) => {
      const area = link.dataset.ikArea;
      const on = base.includes(area);
      link.classList.toggle('is-active', on);
      if (on) link.setAttribute('aria-current', 'true');
      else link.removeAttribute('aria-current');
      link.href = urlFor(on ? base.filter((t) => t !== area) : base.concat(area));
    });
    const allLink = root.querySelector('[data-ik-area-all]');
    if (allLink && onAreasPage) {
      allLink.href = urlFor([]);
      if (base.length) allLink.removeAttribute('aria-current');
      else allLink.setAttribute('aria-current', 'true');
    }
    root.querySelectorAll('[data-ik-area-box]').forEach((box) => {
      box.checked = base.includes(box.value);
    });

    const searching = onAreasPage && term !== '';
    if (!onAreasPage || (!chosen.length && !searching)) {
      root.classList.remove('is-areas-pending');
      return;
    }

    // Links, die Shopify ohne die Bereiche baut (Preis-Schnellwahl, Filter-Chips), behalten die Auswahl
    root.querySelectorAll('.ik-finder__price, .ik-collection__active a').forEach((link) => {
      const url = new URL(link.getAttribute('href'), window.location.href);
      url.searchParams.delete(AREA_PARAM);
      chosen.forEach((t) => url.searchParams.append(AREA_PARAM, t));
      link.setAttribute('href', url.pathname + url.search + url.hash);
    });

    if (searching) {
      applySearchContext(root, term, chosen);
    } else {
      // Überschrift, Seitenpfad und Fenstertitel nennen die Auswahl
      setCatalogTitle(root, areaTitle(chosen));
    }

    filterAreas(root, chosen, searching ? term : '');
  }

  const areaTitle = (list) => (list.length > 2 ? list.slice(0, -1).join(', ') + ' & ' + list[list.length - 1] : list.join(' & '));

  function setCatalogTitle(root, title) {
    const allUrl = root.dataset.ikAllUrl || '/collections/all';
    const heading = root.querySelector('.ik-collection__title');
    if (heading) heading.textContent = title;
    const crumb = root.querySelector('.ik-collection__crumbs [aria-current="page"]');
    if (crumb) {
      crumb.textContent = title;
      if (!root.querySelector('.ik-collection__crumbs [data-ik-crumb-catalog]')) {
        const li = document.createElement('li');
        const link = document.createElement('a');
        link.setAttribute('data-ik-crumb-catalog', '');
        link.href = allUrl;
        link.textContent = 'Katalog';
        li.append(link);
        crumb.before(li);
      }
    }
    document.title = title + ' – ' + document.title.split(/\s[–|-]\s/).pop();
  }

  // Suchbegriff sichtbar machen und in allen Katalog-Links und -Formularen mitführen
  function applySearchContext(root, term, chosen) {
    const quoted = '„' + term + '“';
    setCatalogTitle(root, chosen.length ? quoted + ' in ' + areaTitle(chosen) : 'Suche: ' + quoted);

    root.querySelectorAll('.ik-finder__input').forEach((input) => {
      input.value = term;
    });

    // Filter-Formular (Checkboxen, Preis, Sortierung) sendet den Suchbegriff mit
    root.querySelectorAll('[data-ik-filter-form]').forEach((form) => {
      let hidden = form.querySelector('input[type="hidden"][name="' + SEARCH_PARAM + '"]');
      if (!hidden) {
        hidden = document.createElement('input');
        hidden.type = 'hidden';
        hidden.name = SEARCH_PARAM;
        form.append(hidden);
      }
      hidden.value = term;
    });

    // Bereichs-Knöpfe, Preis-Schnellwahl und Filter-Chips behalten den Suchbegriff
    root.querySelectorAll('.ik-finder__price, .ik-finder__area, .ik-collection__active a, .ik-collection__reset').forEach((link) => {
      const href = link.getAttribute('href');
      if (!href) return;
      const url = new URL(href, window.location.href);
      url.searchParams.set(SEARCH_PARAM, term);
      link.setAttribute('href', url.pathname + url.search + url.hash);
    });

    // „Suche aufheben“ neben der Artikelanzahl
    const count = root.querySelector('.ik-collection__count');
    if (count && !root.querySelector('[data-ik-search-reset]')) {
      const url = new URL(window.location.href);
      url.searchParams.delete(SEARCH_PARAM);
      url.searchParams.delete('page');
      const reset = document.createElement('a');
      reset.className = 'ik-chip ik-collection__search-reset';
      reset.setAttribute('data-ik-search-reset', '');
      reset.href = url.pathname + url.search + '#katalog';
      reset.textContent = quoted;
      const cross = document.createElement('span');
      cross.setAttribute('aria-hidden', 'true');
      cross.textContent = ' ×';
      const label = document.createElement('span');
      label.className = 'visually-hidden';
      label.textContent = ' – Suche aufheben';
      reset.append(cross, label);
      count.after(reset);
    }
  }

  async function filterAreas(root, chosen, term = '') {
    const grid = root.querySelector('.ik-collection__grid');
    const pages = Number(root.dataset.ikPages) || 1;
    const current = Number(root.dataset.ikPage) || 1;
    if (grid && pages > 1 && root.dataset.ikSection) {
      try {
        const byPage = new Map([[current, [...grid.children]]]);
        const requests = [];
        for (let page = 1; page <= pages; page += 1) {
          if (page === current) continue;
          const url = new URL(window.location.href);
          url.hash = '';
          url.searchParams.set('page', String(page));
          url.searchParams.set('section_id', root.dataset.ikSection);
          requests.push(
            fetch(url.toString())
              .then((response) => (response.ok ? response.text() : ''))
              .then((html) => {
                const doc = new DOMParser().parseFromString(html, 'text/html');
                byPage.set(page, [...doc.querySelectorAll('.ik-collection__grid > .ik-grid__item')]);
              })
          );
        }
        await Promise.all(requests);
        const items = [...byPage.keys()].sort((a, b) => a - b).flatMap((page) => byPage.get(page));
        grid.replaceChildren(...items);
        root.querySelector('.ik-collection__pagination')?.setAttribute('hidden', '');
      } catch (error) {
        /* Nachladen fehlgeschlagen: es wird nur die aktuelle Seite gefiltert */
      }
    }

    const words = searchNorm(term).split(' ').filter(Boolean);
    const inTitle = [];
    const elsewhere = [];
    let shown = 0;
    root.querySelectorAll('.ik-collection__grid > .ik-grid__item').forEach((item) => {
      let match = !chosen.length || chosen.includes(item.dataset.ikType);
      let titleHit = false;
      if (match && words.length) {
        const haystack = searchNorm(item.dataset.ikSearch || item.textContent);
        match = words.every((w) => haystack.includes(w));
        titleHit = match && words.every((w) => searchNorm(item.dataset.ikTitle).includes(w));
      }
      item.classList.toggle('is-area-hidden', !match);
      if (match) {
        shown += 1;
        (titleHit ? inTitle : elsewhere).push(item);
      }
    });
    // Treffer im Titel zuerst, sonst Shopifys Reihenfolge
    if (grid && words.length && inTitle.length && elsewhere.length) {
      [...inTitle, ...elsewhere].forEach((item) => grid.append(item));
      grid.querySelectorAll('.ik-grid__item.is-area-hidden').forEach((item) => grid.append(item));
    }
    const count = root.querySelector('.ik-collection__count strong');
    if (count) count.textContent = String(shown);
    root.querySelector('.ik-collection__areas-empty')?.remove();
    if (!shown) {
      const empty = document.createElement('div');
      empty.className = 'ik-collection__empty ik-collection__areas-empty';
      const head = document.createElement('p');
      head.className = 'ik-h3';
      const hint = document.createElement('p');
      hint.className = 'ik-muted';
      const all = document.createElement('a');
      all.className = 'ik-btn ik-btn--dark';
      all.textContent = 'Alle Artikel ansehen';
      all.href = (root.dataset.ikAllUrl || '/collections/all') + '#katalog';
      if (words.length) {
        head.textContent = 'Keine Artikel zu „' + term.trim() + '“ gefunden.';
        hint.textContent = 'Prüfen Sie die Schreibweise oder versuchen Sie einen allgemeineren Begriff, z. B. „Smoker“ oder „Schleier“.';
      } else {
        head.textContent = 'Keine Artikel passen zu Ihrer Auswahl.';
        hint.textContent = 'Wählen Sie weitere Bereiche oder entfernen Sie einzelne Filter.';
      }
      empty.append(head, hint, all);
      root.querySelectorAll('.ik-collection__results > .ik-collection__empty').forEach((el) => el.setAttribute('hidden', ''));
      (grid || root.querySelector('.ik-collection__toolbar')).after(empty);
    }
    root.classList.remove('is-areas-pending');
  }

  /* ------------------------------------------------------------------------
     Start
     ------------------------------------------------------------------------ */
  function init() {
    initSaleTimers();
    initGuaranteeDates();
    initFullpage();
    document.querySelectorAll('section-product.ik-pdp').forEach(initProductPage);
    initReveal();
    document.querySelectorAll('[data-ik-stage]').forEach(initStage);
    initCounters();
    document.querySelectorAll('[data-ik-bees]').forEach(initPromiseBees);
    initSortMenus();
    initRails();
    initAreas();
    if (!window.__ikFloatInit) {
      window.__ikFloatInit = true;
      initFloatingCart();
      initScrollLockSync();
      initCookieDock();
    }
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();

  // Theme-Editor: Sections werden neu geladen
  document.addEventListener('shopify:section:load', init);

  window.ikUi = { formatMoney, renderCartState, scheduleCartRefresh, updateShippingPreview, showToast };
})();
